import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { deleteUser } from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import { auth, db } from "../../../firebase/config";
import Config from "../../../Config";
import { useAuth } from "../../../utils/AuthContext";
import { useSendEmail } from "../../../hooks/useSendEmail";
import { verificationCodeEmail } from "../../../emails";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";

const CODE_LENGTH = 6;

const VerifyCode: React.FC = () => {
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const { completeSignup } = useAuth();
  const { sendEmail } = useSendEmail();

  useEffect(() => {
    const pendingEmail = sessionStorage.getItem("pendingVerificationEmail");
    if (!pendingEmail) {
      navigate("/signup");
      return;
    }
    setEmail(pendingEmail);
    inputRefs.current[0]?.focus();
  }, [navigate]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted.charAt(i);
    }
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < CODE_LENGTH) {
      setError("Please enter the full 6-digit code.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Session expired. Please sign up again.");
        navigate("/signup");
        return;
      }

      const pendingPlan = localStorage.getItem("pendingPlan") || "free";
      // Persist the display name into Postgres at signup. Every new-user
      // path (native, org setup/accept) reaches here and stored the typed name
      // in sessionStorage, so this is the single point that captures it.
      const pendingName =
        sessionStorage.getItem("pendingVerificationName") || undefined;
      const response = await fetch(`${Config.API_URL}/api/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          firebase_uid: user.uid,
          account_type: pendingPlan,
          ...(pendingName ? { name: pendingName } : {}),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || "Invalid or expired code. Please try again.");
        setLoading(false);
        return;
      }

      const data = await response.json();
      const backendToken = data.access_token;

      // Handle pending org setup/accept tokens
      const pendingOrgSetupToken = sessionStorage.getItem(
        "pendingOrgSetupToken",
      );
      const pendingOrgAcceptToken = sessionStorage.getItem(
        "pendingOrgAcceptToken",
      );

      if (pendingOrgSetupToken || pendingOrgAcceptToken) {
        try {
          const firebaseToken = await user.getIdToken(true);

          if (pendingOrgSetupToken) {
            await fetch(`${Config.API_URL}/api/organization/setup`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${backendToken}`,
                "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
              },
              body: JSON.stringify({ token: pendingOrgSetupToken }),
            });
            sessionStorage.removeItem("pendingOrgSetupToken");
          }

          if (pendingOrgAcceptToken) {
            await fetch(
              `${Config.API_URL}/api/organization/accept-invitation`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${backendToken}`,
                  "X-Firebase-Authorization": `Bearer ${firebaseToken}`,
                },
                body: JSON.stringify({ token: pendingOrgAcceptToken }),
              },
            );
            sessionStorage.removeItem("pendingOrgAcceptToken");
          }
        } catch (err) {
          console.error("Failed to complete organization setup/accept:", err);
        }
      }

      sessionStorage.removeItem("pendingVerificationEmail");
      sessionStorage.removeItem("pendingVerificationName");

      if (localStorage.getItem("pendingPlan") === "individual") {
        localStorage.removeItem("pendingPlan");
        localStorage.setItem("payment_pending", "true");
      }

      await completeSignup(backendToken);

      if (localStorage.getItem("payment_pending") === "true") {
        navigate("/card-details");
      } else {
        navigate("/home/dashboard");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleEditEmail = async () => {
    setEditingEmail(true);
    setError("");
    try {
      const user = auth.currentUser;
      if (user) {
        await deleteDoc(doc(db, "users", user.uid));
        await deleteUser(user);
      }
    } catch (err) {
      console.error("Failed to clean up pending user:", err);
    } finally {
      const name = sessionStorage.getItem("pendingVerificationName") || "";
      sessionStorage.removeItem("pendingVerificationEmail");
      sessionStorage.removeItem("pendingVerificationName");
      navigate("/signup", { state: { name, email } });
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage("");
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) {
        setResendMessage("Session expired. Please sign up again.");
        return;
      }
      const response = await fetch(
        `${Config.API_URL}/api/auth/send-verification-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firebase_uid: user.uid }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        // Fire-and-forget: the email is written to Firestore and must not block
        // the "code resent" confirmation shown to the user.
        sendEmail({
          to: email,
          template: verificationCodeEmail({ code: data.code }),
        }).catch((error) => {
          console.error("Error resending verification email:", error);
        });
        setResendMessage("A new code has been sent to your email.");
        setDigits(Array(CODE_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      } else {
        setResendMessage("Failed to resend code. Please try again.");
      }
    } catch {
      setResendMessage("Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-white">
      <div className="w-full max-w-sm px-4">
        <div className="mb-6">
          <Mail className="text-[#1d365d]" size={32} />
        </div>

        <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
          Check your email
        </h2>
        <p className="text-sm text-gray-500 mb-1">We sent a 6-digit code to:</p>
        <div className="flex items-center gap-2 mb-6">
          <p className="text-sm font-medium text-[#1d365d]">{email}</p>
          <button
            type="button"
            onClick={handleEditEmail}
            disabled={editingEmail || loading}
            className="text-xs text-gray-400 hover:text-[#1d365d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed underline"
          >
            {editingEmail ? "Redirecting..." : "Edit"}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-2">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                disabled={loading}
                className="w-full h-12 text-center text-lg font-semibold border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            ))}
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {resendMessage && (
            <p
              className={`text-sm rounded-lg px-3 py-2 border ${
                resendMessage.includes("new code")
                  ? "text-green-600 bg-green-50 border-green-200"
                  : "text-red-600 bg-red-50 border-red-200"
              }`}
            >
              {resendMessage}
            </p>
          )}

          <PrimaryButton type="submit" disabled={loading} className="w-full">
            {loading ? "Verifying..." : "Verify"}
          </PrimaryButton>
        </form>

        <div className="border-t border-gray-100 mt-6 pt-6 text-center">
          <p className="text-sm text-gray-500">
            Didn't receive a code?{" "}
            <button
              onClick={handleResend}
              disabled={resending || loading}
              className="text-[#1d365d] font-medium hover:text-[#2d4a75] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resending ? "Sending..." : "Resend code"}
            </button>
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Code expires in 10 minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyCode;
